import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "ApiClient";
import { ApiURL } from "utils/Actions";
import { settingFields } from "utils/AssetsData";
import { API_RESPONSE_STATUS, CONTACT_FIELD } from "utils/Constants";
import { showErrorModal } from "utils/hooks/handleNotification";
import { SchoolContactTableType } from "utils/Types";
import { common } from "utils/Wording";

type SchoolContactSliceType = {
  data: SchoolContactTableType;
  settingFieldData: SchoolContactTableType;
  isLoading: boolean;
  isLoadingError: boolean;
  isEqual: boolean;
};

const SchoolContactInit: SchoolContactSliceType = {
  data: {} as SchoolContactSliceType["data"],
  settingFieldData: {} as SchoolContactSliceType["settingFieldData"],
  isLoading: false,
  isLoadingError: false,
  isEqual: true
};

const cleanUpSchoolName = (actionData: any) => {
  for (const key in actionData) {
    if (key === "school") {
      const schoolNameValue = actionData.school.schoolName;
      delete actionData["school"];
      actionData[CONTACT_FIELD.SCHOOL_NAME] = schoolNameValue;
    }
    if (typeof actionData[key] == "string") {
      actionData[key] = actionData[key].trim();
    }
  }

  return actionData;
};

const selectSettingFieldsData = (actionData: any) => {
  const settingObj = JSON.parse(JSON.stringify(actionData));

  Object.keys(settingObj).forEach((key: string) => {
    const isSettingKey = settingFields.includes(key);

    if (!isSettingKey) {
      delete settingObj[key];
    }
  });

  return settingObj;
};

export const fetchSchoolContact = createAsyncThunk("/schoolContact", async () => {
  try {
    const res = await api.get(ApiURL.schoolContact);
    if (res?.data?.status === API_RESPONSE_STATUS.ERROR) {
      throw new Error(common.loadingError);
    }
    return res?.data?.data ? res.data.data : [];
  } catch (error: any) {
    console.error(error);
    showErrorModal({
      title: common.failAsync,
      errors: error.response?.data?.errors ? error.response.data.errors : [error.message]
    });
    return SchoolContactInit.data;
  }
});

export const SchoolContactSlice = createSlice({
  name: "school-contact",
  initialState: SchoolContactInit,
  reducers: {
    updateSchoolContact: (state, action) => {
      const actionData = cleanUpSchoolName(action.payload);
      const settingFieldData = selectSettingFieldsData(actionData);
      return {
        ...state,
        isLoading: false,
        isLoadingError: false,
        data: actionData,
        settingFieldData
      };
    },
    setSchoolContactError: state => {
      state.isLoadingError = true;
    },
    setIsEqual: (state, action) => {
      state.isEqual = action.payload;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchSchoolContact.pending, state => {
      state.isLoading = true;
      state.isLoadingError = false;
    });
    builder.addCase(fetchSchoolContact.fulfilled, (state, action) => {
      const actionData = cleanUpSchoolName(action.payload);
      const settingFieldData = selectSettingFieldsData(actionData);

      return {
        ...state,
        data: actionData,
        settingFieldData,
        isLoading: false
      };
    }),
      builder.addCase(fetchSchoolContact.rejected, () => {
        return {
          data: SchoolContactInit.data,
          settingFieldData: SchoolContactInit.settingFieldData,
          isLoading: false,
          isLoadingError: true,
          isEqual: true
        };
      });
  }
});

export const { updateSchoolContact, setSchoolContactError, setIsEqual } =
  SchoolContactSlice.actions;

export default SchoolContactSlice.reducer;
